import { useState } from 'react';
import { categories, formatReads, hotList } from '@/mocks/gossip';

const heatStyle: Record<string, string> = {
  爆: 'bg-primary-500 text-background-50',
  热: 'bg-accent-400 text-foreground-950',
  新: 'bg-secondary-300 text-secondary-950',
};

const editorNotes = [
  { day: '周一', note: '「红毯翻车」补料：新拖尾绊脚素材已上桌' },
  { day: '周三', note: '「撞衫对比」排期：隔空同款头瓜下午出' },
  { day: '周五', note: '「时尚灾难」特别篇：亮片预警请蹲守' },
];

export default function Ranking() {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <section id="hot" className="relative w-full bg-background-50 py-16 md:py-20">
      <div className="mx-auto grid max-w-[1240px] grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-3">
        {/* 左：热瓜榜 */}
        <div className="lg:col-span-2">
          <p className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-accent-600">
            <i className="ri-fire-fill text-base"></i>
            TOP TRENDING
          </p>
          <h2 className="rule-line font-display text-3xl font-black text-foreground-950 md:text-4xl">
            今日热瓜榜 Top 10
          </h2>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-foreground-700">
            围观热度持续跳动的本期头条，点击数字越大的瓜越上头。火候仅供参考，排名全凭大家爱看。
          </p>

          <ol className="mt-8 space-y-3">
            {hotList.map((it, i) => {
              const rank = i + 1;
              const top3 = rank <= 3;
              return (
                <li
                  key={it.id}
                  className="flex items-center gap-4 rounded-xl border border-background-200/80 bg-foreground-50 px-5 py-3.5 transition-colors hover:border-primary-200 hover:bg-primary-50"
                >
                  <span
                    className={`w-9 shrink-0 text-center font-display ${
                      top3 ? 'text-2xl font-black text-primary-500' : 'text-xl font-bold text-foreground-400'
                    }`}
                  >
                    {rank.toString().padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-medium text-foreground-900">{it.title}</h3>
                    <p className="mt-1 flex items-center gap-3 text-xs text-foreground-500">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-eye-line text-secondary-500"></i>
                        {formatReads(it.reads)} 围观
                      </span>
                      <span className="hidden sm:inline">讨论热度持续升温中</span>
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-md px-2 py-1 text-xs font-bold ${heatStyle[it.heat] ?? 'bg-background-100 text-foreground-700'}`}
                  >
                    {it.heat}
                  </span>
                  <a
                    href="#pan"
                    aria-label={`去围观：${it.title}`}
                    className="hidden shrink-0 items-center justify-center rounded-md border border-background-200 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 sm:inline-flex"
                  >
                    围观
                    <i className="ri-arrow-right-line ml-1 text-sm"></i>
                  </a>
                </li>
              );
            })}
          </ol>
        </div>

        {/* 右：小编值班卡 */}
        <aside className="space-y-6">
          <div className="rounded-xl bg-foreground-900 p-6 text-background-50">
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-accent-400 font-display text-2xl font-black text-foreground-950">
                桃
              </span>
              <div>
                <p className="font-display text-lg font-bold">主编 · 桃桃</p>
                <p className="text-xs text-background-100/70">专注红毯造型 8 年的吃瓜小编</p>
              </div>
            </div>
            <p className="mt-5 border-t border-background-50/15 pt-5 text-sm leading-relaxed text-background-100/90">
              「造型没有绝对对错，只有合不合适。我负责帮大家把每一次出场的功课拆开讲清楚，至于美丑，交给你的眼睛来投票。」
            </p>
            <a
              href="#contact"
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-mail-send-line text-base"></i>
              给桃桃来信
            </a>
          </div>

          {/* 本期排期 */}
          <div className="rounded-xl border border-background-200/80 bg-foreground-50 p-6">
            <p className="flex items-center gap-2 font-display text-base font-bold text-foreground-950">
              <i className="ri-calendar-line text-secondary-600"></i>
              本周排期表
            </p>
            <ul className="mt-4 space-y-3">
              {editorNotes.map((n) => (
                <li key={n.day} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex w-12 shrink-0 items-center justify-center rounded-full bg-secondary-100 px-2 py-1 text-xs font-semibold text-secondary-900">
                    {n.day}
                  </span>
                  <p className="text-sm leading-relaxed text-foreground-700">{n.note}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* 快速栏目跳转 */}
          <div className="rounded-xl border border-background-200/80 bg-foreground-50 p-6">
            <p className="flex items-center gap-2 font-display text-base font-bold text-foreground-950">
              <i className="ri-price-tag-3-line text-primary-500"></i>
              想去哪片瓜田？
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {categories.map((c) => (
                <a
                  key={c.id}
                  href="#pan"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                >
                  <i className={`${c.icon} text-sm text-primary-500`}></i>
                  {c.name}
                </a>
              ))}
            </div>
            <button
              type="button"
              onClick={copyLink}
              className={`mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                copied ? 'bg-accent-400 text-foreground-950' : 'bg-background-100 text-foreground-800 hover:bg-accent-100 hover:text-accent-700'
              }`}
            >
              <i className={`${copied ? 'ri-check-line' : 'ri-link'} text-base`}></i>
              {copied ? '已复制本期链接' : '复制本期热榜链接'}
            </button>
          </div>
        </aside>
      </div>
    </section>
  );
}